/**
 * Which pages carry the feedback button, and what "which tab am I on?" resolves to.
 *
 * The list is DERIVED from the nav config rather than hand-written a second time: a page added
 * to Talent Acquisition gets the button automatically. A per-tab feedback route whose tab list
 * silently goes stale is the one failure that matters here — a tab nobody can report on is a tab
 * nobody reports on, and nothing would surface the gap.
 *
 * The resolved surface is what gets pre-filled into the panel and posted to Slack, so it has to
 * name the page the way the SIDEBAR names it. Do not invent labels here.
 */
import { peopleSpaceConfig } from '../config';

export interface FeedbackSurface {
  /** Nav item id — the stable key, safe to group on in Slack or later analytics. */
  id: string;
  /** The label as the sidebar shows it, e.g. "TA Action Boards". */
  label: string;
  /** Sub-tab inside the page (TA Action Boards has four), already prettified, or null. */
  subTab: string | null;
  /** The nav item's own path, without the sub-tab. */
  path: string;
}

/**
 * Pages outside Talent Acquisition that still get the button, by id.
 *
 * Usage Analytics is kept here although it has since moved INTO Talent Acquisition (People
 * Operations → Candidate Communication → Product Experience, Sep 2026), so it is now covered by
 * the group rule below as well. Harmless and deliberate: it was asked for by name, and a page
 * named here keeps its button whichever group it is moved to next.
 */
const EXTRA_SURFACE_IDS = new Set(['usage-analytics']);

/**
 * Nav groups, beyond Talent Acquisition, whose whole membership gets the button. People & Org
 * Planning (Org Planning Overview, Role Approvals), People Analytics (People Cockpit — it had the
 * button while it sat in People & Org Planning, and kept it when it moved out, Oct 2026) and Boris,
 * the Business Partner (Ticket Management, My inquiries, New request, Chat with Boris — David,
 * Oct 2026) are derived the same way TA is — a page added to any of them gets a button
 * automatically, with no second edit here.
 */
const EXTRA_SURFACE_GROUPS = new Set(['people-org-planning', 'people-analytics', 'boris']);

/**
 * Routes that render a surface under a second path. `/log` is the canonical path for the page
 * whose nav item still points at `/interviews` (see PeopleSpace.tsx) — without this the button
 * would vanish on the canonical URL.
 */
const PATH_ALIASES: Record<string, string> = { '/log': '/interviews' };

/** The nav items that get a button, longest path first so nested routes match the deepest page. */
export const feedbackSurfaceItems = peopleSpaceConfig.navItems
  .filter(
    (item) =>
      item.group === 'talent-acquisition' ||
      (item.group != null && EXTRA_SURFACE_GROUPS.has(item.group)) ||
      EXTRA_SURFACE_IDS.has(item.id)
  )
  .slice()
  .sort((a, b) => b.path.length - a.path.length);

/** `first-round` → `First Round`. Sub-tabs are URL slugs; the panel shows them to a human. */
function prettifyTab(segment: string): string {
  return segment
    .replace(/[-_]+/g, ' ')
    .trim()
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

/**
 * The surface for a pathname, or null when this page has no feedback button.
 *
 * Matching is longest-path-first and only ever on a full segment boundary, so `/interview-links`
 * can never be swallowed by a shorter sibling and `/ta-action-boards/requests` resolves to the
 * board plus its sub-tab rather than to the bare board.
 */
export function resolveFeedbackSurface(pathname: string): FeedbackSurface | null {
  const path = PATH_ALIASES[pathname] ?? pathname;
  const match = feedbackSurfaceItems.find(
    (item) => path === item.path || path.startsWith(`${item.path}/`)
  );
  if (!match) return null;

  const rest = path.slice(match.path.length).replace(/^\/+/, '');
  const segment = rest.split('/')[0] ?? '';

  return {
    id: match.id,
    label: match.name,
    subTab: segment ? prettifyTab(segment) : null,
    path: match.path,
  };
}

/** "TA Action Boards › Requests" — one string for the banner and the Slack post. */
export function describeSurface(surface: FeedbackSurface): string {
  return surface.subTab ? `${surface.label} › ${surface.subTab}` : surface.label;
}
